// Core
import React, { useState, useEffect } from 'react';
import {NavLink, useLocation} from 'react-router-dom';
import { AiOutlineEye, AiOutlineEyeInvisible } from 'react-icons/ai';
import {
  AbsoluteCenter,
  Box,
  Flex,
  Button,
  FormControl,
  FormLabel,
  FormHelperText,
  Divider,
  Grid,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Heading,
  Input,
  InputGroup,
  InputRightElement,
  Image,
  IconButton,
  Link,
  Text,
  useDisclosure,
  useColorModeValue,
  useColorMode
} from '@chakra-ui/react';

// Assets
import { apiService } from 'services/ApiService';
import { getLinkLoyaltyMembership } from 'services/Utils';
import logo from 'assets/img/logo.png';
import NotifySuccess from 'components/Form/NotifySuccess';

// Components
import { StarsIcon, EmailIcon, PhoneIcon } from 'components/Icons/Icons';
import FooterAuth from 'components/Footer/Auth';

function Login() {
  const location = useLocation();

  const textColor = useColorModeValue('#0E1633', 'white');
  const borderColor = useColorModeValue('gray.200');
  const topLineDark = 'linear-gradient(90deg, rgba(56, 169, 225, 0.00) 0%, #38A9E1 56.18%, #FFF 68.16%, #38A9E1 82.69%, rgba(56, 169, 225, 0.00) 100%)';
  const topLineWhite = 'linear-gradient(90deg, rgba(56, 169, 225, 0.00) 9.63%, #38A9E1 37.41%, #38A9E1 50%, #38A9E1 60.02%, rgba(56, 169, 225, 0.00) 91.14%)';
  const topLine = useColorModeValue(topLineWhite, topLineDark);
  
  const { colorMode } = useColorMode();
  const isDark = colorMode === 'dark';

  const [show, setShow] = React.useState(false);
  
  const [login, setLogin] = useState('');
  const [password, setPassword] = useState('');

  const [notify, setNotify] = useState();
  const [isValid, setIsValid] = useState(false);
  const [isError, setIsError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isApiError, setIsApiError] = useState(false);
  const [isBlockedError, setIsBlockedError] = useState(false);
  const [isNotActiveError, setIsNotActiveError] = useState(false);
  const [isRestoreError, setIsRestoreError] = useState(false);
  const [isNoSubscriptionError, setIsNoSubscriptionError] = useState(false);
  const [isTooManyRequestsError, setIsTooManyRequestsError] = useState(false);
  const [isValidInputLogin, setIsValidInputLogin] = useState(true);  
  const [isValidInputPassword, setIsValidInputPassword] = useState(true);  

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const successParam = params.get('activated');
    if (successParam) {
      setNotify(successParam);
    }
  }, []);

  const handleLoginChange = (event) => {
    const inputLogin = event.target.value;
    setLogin(inputLogin);
    setIsValid(inputLogin.length > 0 && password.length > 0 ? true : false);
    setIsValidInputLogin(true);
    setIsApiError(false);
    setIsBlockedError(false);
    setIsNotActiveError(false);
    setIsRestoreError(false);
    setIsTooManyRequestsError(false);
  };
  const handlePasswordChange = (event) => {
    const inputPassword = event.target.value;
    setPassword(inputPassword);
    setIsValid(inputPassword.length > 0 && login.length > 0 ? true : false);
    setIsValidInputPassword(true);
    setIsApiError(false);
    setIsBlockedError(false);
    setIsNotActiveError(false);
    setIsRestoreError(false);
    setIsTooManyRequestsError(false);
  };

  const handlePassClick = () => setShow(!show);

  const handleSubmit = async (event) => {
    event.preventDefault();

    setIsBlockedError(false);
    setIsNotActiveError(false);
    setIsNoSubscriptionError(false);
    setIsLoading(true);

    try {
      let data = {
        login,
        password
      };

      if(isRestoreError){
        data['restoreAccount'] = true;
      }

      setIsRestoreError(false);

      const response = await apiService.login(data);
      if(response.statusCode === 200){
        if(response?.data?.token){
          localStorage.setItem('auth', JSON.stringify(response.data));
          setIsLoading(true);
          const redirectTo = location.state?.from || '/discounts';
          window.location.replace(redirectTo);
          setIsLoading(false);
        } else if(response?.data.todo === 'blocked_account'){
          setIsError(true);
          setIsLoading(false); 
          setIsBlockedError(true);
        } else if(response?.data.todo === 'activate_account'){
          setIsError(true);
          setIsLoading(false); 
          setIsNotActiveError(true);
        } else if(response?.data.todo === 'restore_account'){
          setIsError(true);
          setIsLoading(false); 
          setIsRestoreError(true);
        } else if(response?.data.todo === 'no_subscription'){
          setIsError(true);
          setIsLoading(false); 
          setIsNoSubscriptionError(true);
        } else if(response?.data.todo === 'set_password'){
          window.location.href = response?.data?.link;
        }
      } else if(response.statusCode === 429){
        //setIsError(true);
        setIsLoading(false); 
        setIsTooManyRequestsError(true);
      } else {
        setIsError(true);
        setIsLoading(false); 
        setIsApiError(true);
        setIsValidInputLogin(false);
        setIsValidInputPassword(false);
      }
    } catch (error) {
      setIsError(true);
      setIsLoading(false);
      setIsValidInputLogin(false);
      setIsValidInputPassword(false);
      setIsApiError(true);
    }

    if(isError){
      setIsValidInputLogin(false);
      setIsValidInputPassword(false);
    }
  };

  return (
    <Box w='100%' textAlign="center">
      <Box mb={{base: '50px', sm: '60px', md: '79px'}} display="flex" alignItems="center" justifyContent="center">
        <NavLink to="/"><Image src={logo} alt='lmctplus image' maxWidth="184px"/></NavLink>
        { isDark && <Box bg="rgba(64, 155, 255, 0.82)" zIndex="-1" position="absolute" opacity="0.6" filter="blur(70px)" h="300px" w="200px" mt="10%" borderRadius="full" /> }
      </Box>

      <Box position="relative">
        <Text as="h1" variant="header-gradient" 
          fontSize={{base: '32px', sm: '40px', md: '48px'}}
          >
          Welcome to LMCT+</Text>
        <Box bg={topLine} left="50%" transform="translateX(-50%) rotate(180deg)" bottom="-2px" h="1px" position="absolute" w="50%" />
      </Box>

      <Flex 
          display="inline-flex"
          gap="8px"
          mt="8px"
          alignItems="center">
          <StarsIcon fontSize="24px" />
          <Box borderRadius={{base: '8px', md: '16px'}} p="4px 16px" background="white">
            <Text as="h2" variant="header-gradient" fontSize={{base: '26px', sm: '32px', md: '40px'}}>Biggest & best</Text>
          </Box>
          <StarsIcon fontSize="24px" />
      </Flex>
      <Text fontSize={{base: '16px', md: '20px'}} color={textColor} mt="14px">Rewards Club in Australia</Text>

      <Text
        mb='30px'
        ms='0px'
        mt="12px"
        color={isBlockedError ? 'red' : isNotActiveError ? 'red' : isRestoreError ? 'red' : isNoSubscriptionError ? 'red' : isTooManyRequestsError ? 'red' : 'gray.400'}
        fontWeight='400'
        fontSize='16px'>
        { isBlockedError ? (
          <>Your account has been banned</>
        ) : isNotActiveError ? (
          <>Your account has not been activated. We have resent the activation email to your email address.</>
        ) : isRestoreError ? (
          <>You`ve requested to delete your profile. Deletion in: 1 day. Logging in again will cancel the process.</>
        ) : isTooManyRequestsError ? (
          <>Too many login attempts. Please try again in 20 minutes.</>
        ) : isNoSubscriptionError ? (
          <>Looks like your subscription is inactive. Pick a plan to unlock access.</>
        ) : (<></>)}
      </Text>

      <Box maxW={{sm: '100%', md: '81%'}} m="0 auto" pt={{base: '0px', md: '18px'}}>
        <FormControl id="login">
          <Input
            value={login}
            onChange={handleLoginChange}
            borderColor={borderColor}
            className={isValidInputLogin ? 'input-valid' : 'input-invalid'}
            type='text'
            placeholder='Email or username'
            autoComplete="off"
          />
        </FormControl>
        <FormControl id="password" mt="16px">
          <InputGroup>
            <Input
              value={password}
              onChange={handlePasswordChange}
              borderColor={borderColor}
              className={isValidInputPassword ? 'input-valid' : 'input-invalid'}
              mb='8px'
              placeholder='Password'
              autoComplete="off"
              type={show ? 'text' : 'password'}
            />
            <FormHelperText className={isApiError ? 'input-error' : 'hidden'}>User not found or wrong password</FormHelperText>
            <InputRightElement>
              <IconButton
                aria-label={show ? 'Hide password' : 'Show password'}
                icon={show ?  <AiOutlineEye /> : <AiOutlineEyeInvisible />}
                onClick={handlePassClick}
                mt='14px'
                mr='14px'
                color='gray.200'
                fontSize='24px'
                variant='ghost-md'
              />
            </InputRightElement>
          </InputGroup>
        </FormControl>

        <Flex justifyContent="space-between" w="100%" my="16px">
          <Link href="/auth/sign-up"><Text color="#099BFF">Sign Up</Text></Link>
          <Link href="/auth/forgot"><Text color="#099BFF">Forgot your password?</Text></Link>
        </Flex>

        <Button
          onClick={handleSubmit}
          variant="button-md"
          disabled={!isValid}
          isLoading={isLoading}
          className={isLoading ? 'freeze' : ''}
          loadingText="Log In"
          maxW={{base: '100%', sm: '168px'}}
          m="16px auto">Log In</Button>

        <FooterAuth isDark={isDark} mt="76px" />
      </Box>

      { notify && 
        <NotifySuccess text="Registration successful! You can now sign in." setNotify={setNotify} />
      }

    </Box>
  );
}

export default Login;
